import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './mcp.css';
import axios from '../../../utils/axiosConfig';
import {
  Alert,
  Input,
  Select,
  Tag,
  Modal,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  CarOutlined,
  SyncOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

const McpFleet = () => {
  const navigate = useNavigate();
  const { token, currentUser } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncNote, setSyncNote] = useState(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [isBulkAssign, setIsBulkAssign] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [assignmentNotes, setAssignmentNotes] = useState('');

  const canAssignToWorkspaces = ['superadmin', 'admin', 'agency', 'enterprise'].includes(currentUser?.role);

  const buildAuth = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const fetchWorkspaces = useCallback(async () => {
    if (!canAssignToWorkspaces) return;
    try {
      const { data } = await axios.get('/api/vehicle-tracking/workspaces', { headers: buildAuth() });
      setWorkspaces(Array.isArray(data) ? data : data.items || []);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
    }
  }, [buildAuth, canAssignToWorkspaces]);

  const fetchAssignments = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/vehicle-tracking/assignments', { headers: buildAuth() });
      setAssignments(Array.isArray(data) ? data : data.items || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  }, [buildAuth]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/client-vehicle-assignments/my-vehicles', { headers: buildAuth() });
      const rawAssignments = data || [];

      if (rawAssignments.length === 0) {
        setVehicles([]);
        setLoading(false);
        return;
      }

      const dbGroups = rawAssignments.reduce((acc, a) => {
        if (!acc[a.geotab_database]) acc[a.geotab_database] = [];
        acc[a.geotab_database].push(a);
        return acc;
      }, {});

      const all = [];
      for (const [database, dbAssignments] of Object.entries(dbGroups)) {
        try {
          const { data: devData } = await axios.get(`/api/integrations/geotab/devices?databases=${database}`, { headers: buildAuth() });
          const devices = Array.isArray(devData?.devices) ? devData.devices : [];
          const filtered = devices.filter(d => dbAssignments.some(a => a.geotab_device_id === d.id));

          filtered.forEach(d => {
            const activeTo = d.activeTo ? new Date(d.activeTo) : null;
            const isActive = !activeTo || activeTo > new Date() || activeTo.getFullYear() >= 2030;

            const assignment = dbAssignments.find(a => a.geotab_device_id === d.id);

            all.push({
              id: d.id,
              name: d.name || d.description || `Vehicle ${d.id}`,
              geotab_device_id: d.id,
              geotab_database: database,
              geotab_account_name: assignment?.geotab_account_name || '',
              serial_number: d.serialNumber || d.vehicleIdentificationNumber || '',
              license_plate: d.licensePlate || d.licenseState || '',
              vin: d.vehicleIdentificationNumber || '',
              device_type: d.deviceType || '',
              status: isActive ? 'active' : 'inactive',
              active_from: d.activeFrom || null,
              active_to: d.activeTo || null,
              category: isActive ? 'active' : 'pending',
              is_active: isActive,
              assignment_id: assignment?.id || null,
              assigned_at: assignment?.assigned_at || null,
              raw_device: d,
            });
          });
        } catch (err) {
          console.error(`Error fetching vehicles for database ${database}:`, err);
        }
      }

      setVehicles(all);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Could not load fleet data. Check your Geotab connection.');
    } finally {
      setLoading(false);
    }
  }, [buildAuth]);

  useEffect(() => {
    fetchVehicles();
    fetchWorkspaces();
    fetchAssignments();
  }, [fetchVehicles, fetchWorkspaces, fetchAssignments]);

  const vehiclesWithAssignments = useMemo(() => {
    return vehicles.map(v => {
      const a = assignments.find(x => x.geotab_device_id === v.geotab_device_id);
      const w = a ? workspaces.find(x => x.id === a.workspace_id) : null;
      return {
        ...v,
        location: w ? w.name : '',
        assignment_id: a?.id || v.assignment_id || null,
      };
    });
  }, [vehicles, assignments, workspaces]);

  const filteredVehicles = useMemo(() => {
    return vehiclesWithAssignments.filter(v => {
      const term = search.trim().toLowerCase();
      const matchesSearch = !term ||
        v.name?.toLowerCase().includes(term) ||
        v.geotab_device_id?.toLowerCase().includes(term) ||
        v.serial_number?.toLowerCase().includes(term) ||
        v.license_plate?.toLowerCase().includes(term) ||
        v.vin?.toLowerCase().includes(term);

      const matchesStatus = filterStatus === 'all' || v.status === filterStatus;

      const matchesLocation =
        filterLocation === 'all' ||
        (filterLocation === 'unassigned' && (!v.location || v.location === 'N/A')) ||
        v.location === filterLocation;

      return matchesSearch && matchesStatus && matchesLocation;
    });
  }, [vehiclesWithAssignments, search, filterStatus, filterLocation]);

  const counts = useMemo(() => {
    const all = vehiclesWithAssignments.length;
    const active = vehiclesWithAssignments.filter(v => v.status === 'active').length;
    const inactive = vehiclesWithAssignments.filter(v => v.status === 'inactive').length;
    const located = vehiclesWithAssignments.filter(v => v.location && v.location !== 'N/A').length;
    return { all, active, inactive, located };
  }, [vehiclesWithAssignments]);

  const filterStatusList = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
    { key: 'located', label: 'Located', count: counts.located },
  ];

  const locationOptions = useMemo(() => {
    const names = workspaces.map(w => w.name).filter(Boolean);
    const list = ['all', 'unassigned', ...Array.from(new Set(names))];
    return list.map(loc => {
      if (loc === 'all') return { value: 'all', label: 'All Locations' };
      if (loc === 'unassigned') return { value: 'unassigned', label: 'Unassigned' };
      return { value: loc, label: loc };
    });
  }, [workspaces]);

  async function onSync() {
    setSyncing(true);
    setSyncNote(null);
    setError(null);
    try {
      const res = await fetch('/api/integrations/geotab/sync', {
        method: 'POST',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error('Sync failed');
      const data = await res.json();
      const count = data.devicesReturned ?? data.count ?? 0;
      setSyncNote(
        count === 0
          ? 'No vehicles found on your Geotab account yet.'
          : `Synced ${count} vehicle${count === 1 ? '' : 's'} from Geotab. Any new ones appear below.`
      );
      fetchVehicles();
    } catch {
      setError('Could not sync from Geotab. Check your Geotab connection in MCP Connections.');
    } finally {
      setSyncing(false);
    }
  }

  const handleDelete = (vehicle) => {
    Modal.confirm({
      title: 'Delete Vehicle',
      content: `Are you sure you want to remove "${vehicle.name}" from this view?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
        setSelectedRowKeys(prev => prev.filter(k => k !== vehicle.id));
        setSyncNote('Vehicle removed from view');
      },
    });
  };

  const openEditModal = (vehicle) => {
    const activeFrom = vehicle.active_from ? new Date(vehicle.active_from).toLocaleDateString() : '—';
    const activeTo = vehicle.active_to
      ? new Date(vehicle.active_to).toLocaleDateString()
      : 'Currently Active';

    const details = [
      { label: 'Name', value: vehicle.name },
      { label: 'Geotab ID', value: vehicle.geotab_device_id },
      { label: 'Serial Number', value: vehicle.serial_number },
      { label: 'VIN', value: vehicle.vin },
      { label: 'License Plate', value: vehicle.license_plate },
      { label: 'Device Type', value: vehicle.device_type },
      { label: 'Location', value: vehicle.location || 'Unassigned' },
      { label: 'Database', value: `${vehicle.geotab_database}${vehicle.geotab_account_name ? ` · ${vehicle.geotab_account_name}` : ''}` },
      { label: 'Status', value: vehicle.status === 'active' ? 'Active' : 'Inactive' },
      { label: 'Active From', value: activeFrom },
      { label: 'Active To', value: vehicle.status === 'active' ? 'Currently Active' : activeTo },
    ].filter(item => item.value && item.value !== '—');

    Modal.info({
      title: 'Vehicle Details',
      width: 600,
      content: (
        <div>
          {details.map((item, index) => (
            <p key={index} style={{ marginBottom: 8 }}>
              <strong style={{ color: '#2A3656' }}>{item.label}:</strong>{' '}
              <span className="text-ink-100">{item.value}</span>
            </p>
          ))}
          {workspaces.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <button
                type="button"
                className="mcp-btn-sun"
                onClick={() => {
                  Modal.destroyAll();
                  openAssignModal(vehicle);
                }}
              >
                <EnvironmentOutlined /> Assign to Location
              </button>
            </div>
          )}
        </div>
      ),
      okText: 'Close',
    });
  };

  const openAssignModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedVehicles([vehicle]);
    setIsBulkAssign(false);
    setAssignModalVisible(true);
  };

  const openBulkAssignModal = () => {
    if (selectedRowKeys.length === 0) {
      setError('Please select at least one vehicle');
      return;
    }
    const selected = vehiclesWithAssignments.filter(v => selectedRowKeys.includes(v.id));
    setSelectedVehicles(selected);
    setSelectedVehicle(null);
    setIsBulkAssign(true);
    setAssignModalVisible(true);
  };

  const handleAssignLocation = async () => {
    if (!selectedLocationId) {
      setError('Please select a location');
      return;
    }

    const toAssign = isBulkAssign ? selectedVehicles : [selectedVehicle];
    let success = 0;
    let fail = 0;

    for (const vehicle of toAssign) {
      try {
        await axios.post('/api/vehicle-tracking/assign', {
          geotab_device_id: vehicle.geotab_device_id,
          location_id: selectedLocationId || null,
          workspace_id: selectedLocationId || null,
          notes: assignmentNotes || null,
        }, { headers: buildAuth() });
        success++;
      } catch (err) {
        console.error(`Error assigning vehicle ${vehicle.name}:`, err);
        fail++;
      }
    }

    if (success > 0) setSyncNote(`${success} vehicle(s) assigned successfully`);
    if (fail > 0) setError(`${fail} vehicle(s) failed to assign`);

    setAssignModalVisible(false);
    setSelectedVehicle(null);
    setSelectedVehicles([]);
    setSelectedLocationId(null);
    setAssignmentNotes('');
    setIsBulkAssign(false);
    setSelectedRowKeys([]);
    fetchAssignments();
  };

  const toggleRow = (id) => {
    setSelectedRowKeys(prev =>
      prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );
  };

  return (
    <div className="mcp-page">
      <McpHeader title="Fleet" />

      <div className="mx-auto w-full max-w-6xl p-6 pb-12">
        {/* Header */}
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl font-bold text-ink">Your fleet</h2>
            <p className="mt-1 text-sm text-ink-200">
              {loading ? 'Loading…' : `${counts.active} active · ${counts.located} located · ${counts.all} total`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Tooltip
              title="Pull latest devices from your Geotab account"
              color="#F4ECDD"
              overlayInnerStyle={{ color: '#2A3656', fontSize: 12 }}
            >
              <button
                type="button"
                className="mcp-btn-ghost"
                onClick={onSync}
                disabled={syncing}
              >
                <SyncOutlined spin={syncing} /> {syncing ? 'Syncing…' : 'Check for new vehicles'}
              </button>
            </Tooltip>
            <button
              type="button"
              className="mcp-btn-ghost"
              onClick={fetchVehicles}
              disabled={loading}
            >
              <ReloadOutlined /> {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button
              type="button"
              className="mcp-btn-sun"
              onClick={openBulkAssignModal}
              disabled={selectedRowKeys.length === 0 || !canAssignToWorkspaces}
            >
              <EnvironmentOutlined /> Assign {selectedRowKeys.length > 0 ? `${selectedRowKeys.length}` : ''} to Location
            </button>
            <button
              type="button"
              className="mcp-btn-ghost"
              onClick={() => navigate('/client/mcp/billing')}
            >
              Manage slots →
            </button>
          </div>
        </div>

        {syncNote && (
          <Alert
            message={syncNote}
            type="success"
            showIcon
            closable
            style={{ marginTop: 24, borderRadius: 12 }}
            onClose={() => setSyncNote(null)}
          />
        )}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            style={{ marginTop: 24, borderRadius: 12 }}
            onClose={() => setError(null)}
            action={<button type="button" className="mcp-btn-ghost" onClick={() => navigate('/client/mcp/connections')}>Fix connections</button>}
          />
        )}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: 'Total', value: counts.all, color: 'blue' },
            { label: 'Active', value: counts.active, color: 'green' },
            { label: 'Inactive', value: counts.inactive, color: 'red' },
            { label: 'Located', value: counts.located, color: 'cyan' },
          ].map(s => (
            <div key={s.label} className="mcp-card3d p-4 text-center">
              <div className="font-display text-2xl font-bold text-ink">{s.value}</div>
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-200">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {filterStatusList.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilterStatus(f.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition ${
                  filterStatus === f.key
                    ? 'border-ink bg-ink text-paper'
                    : 'border-paper-300 bg-paper text-ink hover:bg-paper-200'
                }`}
              >
                {f.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  filterStatus === f.key ? 'bg-paper/20 text-paper' : 'bg-paper-200 text-ink'
                }`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Select
              value={filterLocation}
              onChange={setFilterLocation}
              style={{ width: 180 }}
              options={locationOptions}
              className="mcp-select"
            />
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-200" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, VIN, plate..."
                className="w-64 rounded-full border-2 border-paper-300 bg-paper py-1.5 pl-9 pr-4 text-sm text-ink outline-none placeholder:text-ink-200 focus:border-ink/30"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-3xl border border-paper-300 bg-white shadow-card">
          {/* Table header */}
          <div className="grid grid-cols-[2.5rem_2fr_1.5fr_1.25fr_1fr_1fr] items-center border-b border-paper-300 bg-paper-50 px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-200">
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={filteredVehicles.length > 0 && filteredVehicles.every(v => selectedRowKeys.includes(v.id))}
                onChange={() => {
                  if (filteredVehicles.every(v => selectedRowKeys.includes(v.id))) {
                    setSelectedRowKeys([]);
                  } else {
                    setSelectedRowKeys(filteredVehicles.map(v => v.id));
                  }
                }}
                className="h-4 w-4 accent-brand"
              />
            </div>
            <div>Vehicle</div>
            <div>Status</div>
            <div>Last seen</div>
            <div>Location</div>
            <div className="text-right">Actions</div>
          </div>

          {loading ? (
            <div className="px-6 py-16 text-center text-sm text-ink-200">Loading your fleet…</div>
          ) : filteredVehicles.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <CarOutlined style={{ fontSize: 40, color: 'var(--ink-300)', marginBottom: 12 }} />
              <div className="font-bold text-ink">No vehicles yet</div>
              <div className="mt-1 text-sm text-ink-200">
                Connect Geotab to sync your fleet, or add vehicles manually from Geotab.
              </div>
              <div className="mt-4">
                <button type="button" className="mcp-btn-ghost" onClick={onSync}><SyncOutlined /> Sync from Geotab</button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-paper-200">
              {filteredVehicles.map(v => {
                const isActive = v.status === 'active';
                return (
                  <div
                    key={String(v.id || v.geotab_device_id || v.name)}
                    className="grid grid-cols-[2.5rem_2fr_1.5fr_1.25fr_1fr_1fr] items-center px-6 py-4 text-sm transition hover:bg-paper-50"
                  >
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={selectedRowKeys.includes(v.id)}
                        onChange={() => toggleRow(v.id)}
                        className="h-4 w-4 accent-brand"
                      />
                    </div>
                    {/* Vehicle name with colored dot */}
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isActive ? 'bg-sage/15' : 'bg-ember/10'}`}>
                        <CarOutlined className={isActive ? 'text-sage' : 'text-ember'} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-ink">{v.name}</div>
                        <div className="truncate text-xs text-ink-200">{v.license_plate || v.serial_number || v.vin || '—'}</div>
                      </div>
                    </div>
                    {/* Status pill */}
                    <div>
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${isActive ? 'bg-sage/15 text-sage' : 'bg-ember/10 text-ember'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-sage' : 'bg-ember'}`} />
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {/* Last seen */}
                    <div className="text-ink-100">
                      {v.last_seen ? new Date(v.last_seen).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                    </div>
                    {/* Location */}
                    <div>
                      {!v.location || v.location === 'N/A' ? (
                        <span className="text-xs font-medium text-ink-200">Unassigned</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-100">
                          <EnvironmentOutlined className="text-ink-200" />
                          {v.location}
                        </span>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(v)}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-ink-100 transition hover:bg-paper-200 hover:text-ink"
                      >
                        <EditOutlined /> View
                      </button>
                      <Popconfirm
                        title="Delete this vehicle?"
                        description="This removes it from this view only."
                        onConfirm={() => handleDelete(v)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-ember transition hover:bg-ember/10"
                        >
                          <DeleteOutlined /> Delete
                        </button>
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Assign Modal */}
      <Modal
        title={
          isBulkAssign
            ? `Assign ${selectedVehicles.length} Vehicle(s) to Location`
            : 'Assign Vehicle to Location'
        }
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedVehicle(null);
          setSelectedVehicles([]);
          setSelectedLocationId(null);
          setAssignmentNotes('');
          setIsBulkAssign(false);
        }}
        onOk={handleAssignLocation}
        okText={isBulkAssign ? `Assign ${selectedVehicles.length}` : 'Assign'}
        width={500}
      >
        {(selectedVehicle || selectedVehicles.length > 0) && (
          <div>
            {isBulkAssign ? (
              <div style={{ marginBottom: 16 }}>
                <strong className="text-ink">Selected Vehicles ({selectedVehicles.length}):</strong>
                <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                  {selectedVehicles.map(vehicle => (
                    <Tag key={vehicle.id} style={{ margin: 2 }}>{vehicle.name}</Tag>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ marginBottom: 16 }}>
                <strong className="text-ink">Vehicle:</strong>{' '}
                <span className="text-ink-100">{selectedVehicle?.name}</span>
              </p>
            )}

            <div style={{ marginBottom: 16 }}>
              <strong className="text-ink">Location:</strong>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Select a location"
                allowClear
                value={selectedLocationId}
                onChange={setSelectedLocationId}
              >
                {workspaces.map(workspace => (
                  <Select.Option key={workspace.id} value={workspace.id}>
                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                    {workspace.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong className="text-ink">Notes (optional):</strong>
              <Input.TextArea
                style={{ marginTop: 8 }}
                placeholder="Add any notes about this assignment..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default McpFleet;
